import React, { useState } from 'react';
import styled from 'styled-components';
import OnboardingLayout from '../../components/Layout/Onboarding/OnboardingLayout';
import BasicDetails from './BasicDetails/BasicDetails';
import ScreenLoader from '../../components/Common/ScreenLoader';
import KYC from './KYC/KYC';
import { Button, CircularProgress } from '@mui/material';
import ErrorLogger from '../../components/Common/ErrorLogger';
import { useOnboarding } from '../../contexts/OnboardingContext';

export const NextButton = ({
  onClick,
  loading,
  error,
  editingMode,
  children = 'Continue',
  id,
  disabled,
  ...rest
}) => {
  const { ButtonWrapper } = SharedStyledComponents;
  const [forceShow, setForceShow] = useState(false);

  const handleClick = async () => {
    if (onClick) {
      setForceShow(true);
      await onClick();
      setTimeout(() => {
        setForceShow(false);
      }, 1000);
    }
  };

  return (
    <ButtonWrapper>
      <Button
        fullWidth
        variant="contained"
        onClick={handleClick}
        disabled={loading || disabled}
        id={id}
        {...rest}
      >
        <ErrorLogger error={error} forceShow={forceShow} />
        {loading ? <CircularProgress style={{ color: '#fff' }} size={26} /> : children}
      </Button>
    </ButtonWrapper>
  );
};

const OnboardingPage = () => {
  const { loading, activeCat } = useOnboarding();

  return (
    <Wrapper>
      {loading ? (
        <ScreenLoader />
      ) : activeCat === 'kyc' ? (
        <KYC />
      ) : (
        <BasicDetails />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.section`
  min-height: 30vh;
  height: 100%;
  .progress-wrapper {
    position: absolute;
    width: 43px;
    height: 43px;
    right: 14px;
    top: 22px;
    background: #eaf8ff;
    border-radius: 50%;
    .MuiCircularProgress-svg {
      color: #36b37e;
    }
    .pg {
      position: absolute;
      color: #253858;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      font-size: 16px;
      font-weight: bold;
    }
  }
  @media all and (max-width: 768px) {
    .progress-wrapper {
      position: absolute;
      top: -63px;
      right: 32px;
    }
  }
`;

export const SharedStyledComponents = {
  FormWrapper: styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    min-height: 30vh;
  `,

  ContentWrapper: styled.div`
    height: 100%;
    .progress-step {
      position: absolute;
      right: 14px;
      top: 22px;
      .step-text {
        display: none;
      }
    }

    // wrapper for space between content and button
    .flex-wrapper {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    // Common header styles used across all steps
    .steps-header {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      width: 90%;
      margin: 0px 0 20px;

      h3 {
        color: #253858;
        position: relative;
        font-family: Roboto;
        font-size: 28px;
        font-style: normal;
        font-weight: 400;
        line-height: 42px;
        margin-bottom: 10px;
        padding-bottom: 20px;
        margin-top: 0;

        strong {
          font-size: 28px;
          font-weight: 600;
          line-height: 42px;
        }

        &::after {
          content: '';
          position: absolute;
          left: 0;
          background-color: #00a3bf;
          width: 88px;
          height: 2px;
          bottom: 0;
          margin-bottom: 14px;
        }
      }

      h4 {
        color: #253858;
        font-size: 16px;
        font-weight: 700;
        line-height: 24px;
        letter-spacing: 0.256px;
        margin-bottom: 8px;
        margin-top: 0;
      }

      .title,
      .title-para {
        color: rgba(37, 56, 88, 0.7);
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: 0.256px;
        margin: 0;
      }
    }

    // Common form styles
    .form-steps {
      width: 100%;
      max-width: 600px;
      margin: 0 auto;
    }

    // Common input styles
    .MuiInputBase-root {
      margin-bottom: 20px;
      border-radius: 16px;

      .MuiOutlinedInput-notchedOutline {
        border-color: #5e6c84;
      }
    }

    // Common business details styles
    .business-details {
      margin-bottom: 30px;
      p {
        color: #666;
        font-size: 16px;
        line-height: 1.5;
      }
    }

    // Common input wrapper styles
    .input-wrapper {
      display: flex;
      align-items: center;
      border: 1px solid #ccc;
      border-radius: 12px;
      padding: 3px;
      margin-bottom: 10px;
    }
    @media all and (max-width: 768px) {
      .steps-header {
        .title,
        .title-para {
          margin: 0;
        }
      }
    }
  `,

  BackButton: styled.div`
    width: 40px;
    height: 40px;
    border: 2px solid #516079;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: -63px;
    left: 0;
    cursor: pointer;
    @media all and (max-width: 768px) {
      left: 32px;
    }
    svg {
      fill: #516079;
      font-size: 20px;
    }
  `,

  ButtonWrapper: styled.div`
    margin-top: 20px;
  `,

  // Common Input Styles
  InputBase: styled.div`
    input {
      width: 100%;
      padding: 15px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 16px;

      &::placeholder {
        color: #666;
      }

      &:focus {
        outline: none;
        border-color: #0066ff;
      }
    }
  `,

  // Common List Styles
  ListBase: styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 16px;
    }
  `,

  // Common Checkbox Styles
  CheckboxBase: styled.div`
    margin-top: 20px;

    p {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #253858;
      font-size: 14px;
    }

    input[type='checkbox'] {
      width: 18px;
      height: 18px;
      margin: 0;
      cursor: pointer;
    }
  `,

  // Add Address specific base styles
  AddressBase: styled.div`
    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        margin-bottom: 16px;
      }
    }

    .checkbox-col {
      margin-top: 20px;

      p {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #253858;
        font-size: 14px;
      }
    }
  `,

  // Common Button Styles
  SubmitButton: styled.button`
    width: 100%;
    background: #0066ff;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 15px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    margin-top: 30px;

    &:hover {
      background: #0052cc;
    }
  `,
  ReviewWrapper: styled.div`
    .review-details {
      @media all and (max-width: 768px) {
        width: -webkit-fill-available;
      }
      h3 {
        font-size: 28px;
        line-height: 42px;
        font-weight: 600;
        position: relative;
        margin-bottom: 14px;
        padding-bottom: 8px;
        &::after {
          content: '';
          position: absolute;
          left: 0;
          background-color: #00a3bf;
          width: 88px;
          height: 2px;
          bottom: 0;
        }
      }
      p {
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        color: #253858b2;
      }
      button {
        &.submit-btn {
          background-color: #0065ff;
          color: #fff;
          font-size: 16px;
          border: none;
          padding: 0 8px;
          border-radius: 16px;
          width: 100%;
          text-align: center;
          height: 48px;
          line-height: 48px;
          margin: 10px 0;
          cursor: pointer;
        }
      }
      ul.detail-list {
        border-bottom: 1px solid #dfe1e6;
        padding-bottom: 10px;
        margin-bottom: 10px;
        padding-left: 0;
        &:last-child {
          border-bottom: none;
          padding-bottom: 0px;
          margin-bottom: 0px;
        }
        .title-heading {
          padding: 10px 0;
          color: #253858;
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 24px; /* 171.429% */
          letter-spacing: 0.224px;
        }
        .btn-block {
          display: flex;
          gap: 0 6px;
          span.chip-btn {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 14px;
            padding: 6px 8px;
            color: #253858;
            background-color: #deebff99;
            border-radius: 8px;
            border: none;
            cursor: pointer;
            button {
              background-color: transparent;
              border: none;
              height: 14px;
              margin-left: 4px;
            }
          }
        }
        .use-case-header {
          font-size: 14px;
          font-weight: 400;
          line-height: 24px;
          color: #253858b2;
          display: block;
        }

        div {
          position: relative;
          button.edit-btn {
            cursor: pointer;
            color: #0065ff;
            background-color: transparent;
            border: none;
            position: absolute;
            right: 8px;
            top: 0;
            bottom: 0;
            margin: auto;
            font-size: 22px;
            height: 20px;
          }
          &.list {
            display: flex;
            justify-content: flex-start;
            margin: 10px 0;
            align-items: flex-start;
            gap: 0 10px;
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;
            padding-right: 36px;
            a {
              color: #0052cc;
              text-decoration: none;
              cursor: pointer;
              text-overflow: ellipsis;
              overflow: hidden;
            }
            li {
              list-style: none;
              width: 50%;
              span {
                font-size: 14px;
                font-weight: 400;
                line-height: 24px;
                color: #253858b2;
                display: block;
              }
              strong {
                font-size: 16px;
                font-weight: 400;
                line-height: 24px;
                color: #253858;
                text-overflow: ellipsis;
                overflow: hidden;
                width: 100%;
                display: block;
              }
            }
          }
        }
      }
      .verified {
        color: #36b37e !important;
        font-weight: 600 !important;
        font-size: 14px !important;
      }
    }
  `,
  SuccessWrapper: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0px;

    .detail-saved {
      text-align: center;
      // max-width: 600px;
      width: 100%;
      background: #fff;
      border-radius: 12px;
      padding: 0px;
    }

    .head {
      margin-bottom: 40px;

      .success-icon {
        margin-bottom: 24px;

        img {
          width: 80px;
          height: 80px;
        }
      }

      h3 {
        font-size: 24px;
        color: #253858;
        margin-bottom: 12px;
      }

      p {
        color: #666;
        font-size: 16px;
        margin: 0;
      }
    }

    .what-next {
      background: #f8faff;
      border-radius: 12px;
      padding: 32px;

      h4 {
        color: #666;
        font-size: 16px;
        margin-bottom: 16px;
      }

      h3 {
        font-size: 20px;
        color: #253858;
        margin-bottom: 12px;
      }

      .success-message {
        color: #4caf50;
        font-size: 16px;
        margin-bottom: 24px;
      }

      .proceed-btn {
        background: #4c6fff;
        color: white;
        border: none;
        border-radius: 8px;
        padding: 16px 32px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        width: 100%;
        transition: background 0.2s;

        &:hover {
          background: #3955cc;
        }
      }
    }
  `,
};

export default OnboardingPage;
